/**
 * Hook de conexão WebChat via Realtime Broadcast
 * 
 * Conecta diretamente ao canal de broadcast do Supabase para receber
 * mensagens em tempo real sem intermediário WebSocket.
 * 
 * Canal: webchat-visitor-{visitorId}
 * Evento: new_message
 * 
 * @version 5.0.0 - Estabilização de callbacks com refs para evitar
 *                   ciclos de reconexão após logout/login
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import type { ChatMessage, MessageAttachment, InteractiveButton } from '../types';

// Logger para diagnóstico
const logger = createLogger('WebchatConnection');

// Interface do payload recebido via broadcast
interface BroadcastPayload {
  id: string;
  content: string;
  direction: 'inbound' | 'outbound';
  sender_type: 'contact' | 'agent' | 'bot' | 'system';
  sent_at: string;
  status?: string;
  attachments?: Array<{
    type: string;
    url: string;
    filename?: string;
    mime_type?: string;
  }>;
  buttons?: Array<{
    id: string;
    text: string;
    type?: string;
    url?: string;
  }>;
  metadata?: Record<string, unknown>;
  // Dados do agente para widget interno
  sender_name?: string;
  sender_avatar?: string;
}

// Props do hook
interface UseWebchatConnectionProps {
  conversationId: string | null;
  visitorId: string | null;
  inboxId: string | null;
  onNewMessage: (message: ChatMessage) => void;
  onTyping?: (isTyping: boolean) => void;
}

/**
 * Função pura para transformar payload do broadcast em ChatMessage.
 * Extraída do hook para eliminar dependência volátil de useCallback.
 */
function transformPayloadToMessage(payload: BroadcastPayload): ChatMessage {
  // Converte attachments para formato do widget
  const attachments: MessageAttachment[] | undefined = payload.attachments?.map(att => ({
    type: att.type as 'image' | 'document' | 'audio' | 'video',
    url: att.url,
    filename: att.filename,
    mime_type: att.mime_type,
  }));

  // Converte buttons para formato do widget
  const buttons: InteractiveButton[] | undefined = payload.buttons?.map(btn => ({
    id: btn.id,
    text: btn.text,
    type: btn.type as 'reply' | 'url' | undefined,
    url: btn.url,
  }));

  return {
    id: payload.id,
    content: payload.content || '',
    direction: payload.direction,
    sender_type: payload.sender_type,
    sent_at: payload.sent_at,
    status: payload.status as ChatMessage['status'],
    attachments,
    buttons,
    metadata: payload.metadata as ChatMessage['metadata'],
    sender_name: payload.sender_name,
    sender_avatar: payload.sender_avatar,
  };
}

/**
 * Hook para gerenciar conexão Realtime Broadcast do webchat.
 * Utiliza refs para callbacks voláteis, garantindo que o canal
 * só é recriado quando visitorId ou inboxId mudam de fato.
 */
export const useWebchatConnection = ({
  conversationId,
  visitorId,
  inboxId,
  onNewMessage,
  onTyping,
}: UseWebchatConnectionProps) => {
  // Estado da conexão
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  
  // Refs para gerenciar canal e reconexão
  const channelRef = useRef<RealtimeChannel | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  
  // IDs processados para evitar duplicatas
  const processedMessageIds = useRef<Set<string>>(new Set());

  // (removido: lastReconnectAt - heartbeatCallback resolve corridas no transporte)

  // =========================================================================
  // Refs para callbacks voláteis - evita reconexão quando callbacks mudam
  // Padrão: atualiza ref a cada render sem disparar useEffect de conexão
  // =========================================================================
  const onNewMessageRef = useRef(onNewMessage);
  const onTypingRef = useRef(onTyping);

  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
    onTypingRef.current = onTyping;
  });

  /**
   * Conecta ao canal de broadcast.
   * Dependências estáveis: não depende de callbacks externos.
   */
  const connectToChannel = useCallback((vid: string) => {
    // Limpa canal anterior se existir
    if (channelRef.current) {
      logger.debug('Removendo canal anterior');
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channelName = `webchat-visitor-${vid}`;
    // heartbeatCallback + Web Worker cuidam da reconexão do transporte
    logger.info(`Conectando ao canal: ${channelName}`);

    // Cria canal de broadcast
    const channel = supabase.channel(channelName);

    // Escuta evento de nova mensagem
    channel.on('broadcast', { event: 'new_message' }, (payload) => {
      logger.debug('Broadcast recebido:', payload);
      
      const messagePayload = payload.payload as BroadcastPayload;
      
      // Verifica se já processamos esta mensagem
      if (processedMessageIds.current.has(messagePayload.id)) {
        logger.debug(`Mensagem ${messagePayload.id} já processada, ignorando`);
        return;
      }
      
      // Ignora mensagens inbound (são do próprio visitante)
      if (messagePayload.direction === 'inbound') {
        logger.debug('Mensagem inbound ignorada (próprio visitante)');
        return;
      }
      
      // Marca como processada
      processedMessageIds.current.add(messagePayload.id);
      
      // Transforma e envia para o callback via ref (estável)
      const message = transformPayloadToMessage(messagePayload);
      logger.info(`Nova mensagem do bot: ${message.content.substring(0, 50)}...`);
      onNewMessageRef.current(message);
    });

    // Escuta evento de typing do agente/bot
    channel.on('broadcast', { event: 'typing' }, (payload) => {
      logger.debug('Evento typing recebido:', payload);
      
      const typingPayload = payload.payload as { isTyping: boolean };
      
      // Notifica o widget sobre o estado de digitação via ref (estável)
      onTypingRef.current?.(typingPayload.isTyping ?? true);
    });

    // Inscreve no canal
    channel.subscribe((status, error) => {
      logger.debug(`Status do canal: ${status}`, error);
      
      if (status === 'SUBSCRIBED') {
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;
        logger.info(`Conectado ao canal ${channelName}`);
      } else if (status === 'CHANNEL_ERROR') {
        setIsConnected(false);
        setConnectionError('Erro na conexão do canal');
        logger.error('Erro no canal:', error);
        scheduleReconnect(vid);
      } else if (status === 'TIMED_OUT') {
        setIsConnected(false);
        setConnectionError('Timeout na conexão');
        logger.warn('Timeout no canal');
        scheduleReconnect(vid);
      } else if (status === 'CLOSED') {
        setIsConnected(false);
        logger.info('Canal fechado');
      }
    });

    channelRef.current = channel;
  }, []); // Sem dependências voláteis - callbacks acessados via refs

  /**
   * Agenda reconexão com backoff exponencial.
   * Recebe visitorId como parâmetro para evitar dependência de closure.
   */
  const scheduleReconnect = useCallback((vid: string) => {
    if (reconnectAttempts.current >= maxReconnectAttempts) {
      logger.error('Máximo de tentativas de reconexão atingido');
      setConnectionError('Não foi possível reconectar. Recarregue a página.');
      return;
    }

    // Limpa timeout anterior
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Calcula delay com backoff exponencial (1s, 2s, 4s, 8s, 16s)
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 16000);
    reconnectAttempts.current++;

    logger.info(`Reconectando em ${delay}ms (tentativa ${reconnectAttempts.current})`);

    reconnectTimeoutRef.current = setTimeout(() => {
      connectToChannel(vid);
    }, delay);
  }, [connectToChannel]);

  /**
   * Conecta ao canal (função pública)
   */
  const connect = useCallback(() => {
    if (!visitorId) {
      logger.warn('visitorId não disponível para conexão');
      return;
    }
    connectToChannel(visitorId);
  }, [visitorId, connectToChannel]);

  /**
   * Desconecta do canal
   */
  const disconnect = useCallback(() => {
    logger.info('Desconectando...');
    
    // Limpa timeout de reconexão
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Remove canal
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    
    setIsConnected(false);
    reconnectAttempts.current = 0;
  }, []);

  // =========================================================================
  // Efeito principal de conexão - depende APENAS de visitorId e inboxId
  // Não reconecta quando callbacks mudam (acessados via refs)
  // =========================================================================
  useEffect(() => {
    if (visitorId && inboxId) {
      connectToChannel(visitorId);
    }

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitorId, inboxId]);

  // =========================================================================
  // Listener de auth - apenas trata SIGNED_OUT para desconectar canal.
  // SIGNED_IN não precisa reconectar: o heartbeatCallback + Web Worker
  // no cliente Supabase reconectam o transporte automaticamente.
  // =========================================================================
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        logger.info('Auth SIGNED_OUT detectado, desconectando canal...');
        disconnect();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitorId, inboxId]);

  // =========================================================================
  // Listener de visibilidade da aba - segurança adicional
  // Com heartbeatCallback + Web Worker, o transporte se auto-reconecta.
  // Este listener é uma camada extra: verifica se o canal ainda está
  // inscrito após retornar à aba. Usa delay para dar tempo ao heartbeat.
  // =========================================================================
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && visitorId && inboxId) {
        // Delay de 2s para dar tempo ao heartbeatCallback reconectar o transporte
        setTimeout(() => {
          // Só reconecta se o canal NÃO está mais inscrito (heartbeat não resolveu)
          const currentState = channelRef.current?.state;
          if (currentState !== 'joined') {
            logger.info('Aba voltou ao foco e canal não está joined (state: ' + currentState + '). Reconectando...');
            reconnectAttempts.current = 0;
            connectToChannel(visitorId);
          } else {
            logger.debug('Aba voltou ao foco - canal já está joined, nenhuma ação necessária');
          }
        }, 2000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitorId, inboxId]);

  // Limpa IDs processados quando conversationId muda
  useEffect(() => {
    if (conversationId) {
      processedMessageIds.current.clear();
    }
  }, [conversationId]);

  return {
    isConnected,
    connectionError,
    connect,
    disconnect,
  };
};
